import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import { T, F, th as palette, I, Btn, Wordmark } from "@/lib/bricks";
import api from "@/lib/api";
import BricksLoader from "@/components/BricksLoader";
import InfoTip from "@/components/ui/info-tip";
import { AI_ADDONS, FEATURE_ROWS, PLAN_DATA, RECOMMENDED_PLAN, includedCount, money, planValueLine, rowState } from "@/lib/plans";
import { useAuth } from "@/contexts/AuthContext";
import { SOURCE_LABELS } from "@/lib/applicantSource";

// Vendor-application list minus the hiring-only boards.
const HEARD_OPTIONS = Object.entries(SOURCE_LABELS).filter(([k]) => k !== "indeed" && k !== "craigslist_kijiji");
import { markFinalePending, buildWallThenGo } from "@/lib/wallFinale";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { readSignupIntent, clearSignupIntent, isDevSignupType } from "@/lib/signupIntent";
import { propertyCopy } from "@/lib/clientTypes";

// Locked hybrid ladder (Kam 2026-09-05): premade packages + modular AI add-ons.
// Clay = desk + listing file + TMS + BYO MLS. Keystone = CRM + e-sign.
// Marble = Forms. Diamond = AI included (also sold as add-ons on lower plans).
// Deal file is never a paywall. Accounting is office-seat only.
// Media is à la carte — volume curve at booking, never a credit pool.
// Display copy matches BILLING_PACKAGES.
const OFFICE_INCLUDES = [
  'CRM', 'Transactions', 'Checklist e-sign + shareable links', 'Forms', 'BYO MLS', 'Trust accounting',
];
const SEAT_MAX = 1000;
// Team plan covers 6 agents (common/team_seats.py); more need add-on seats.
const TEAM_INCLUDED = 6;

// Monthly billing per account type. Individual + Team use the live monthly Clay–Diamond Prices.
// Brokerage monthly (Kam 2026-10-12): yearly seat rate +25%, month-to-month, no lock-in.
// Mirrors backend BROKERAGE_MONTHLY_SEAT_CENTS.
const MONTHLY_ENABLED = { solo: true, team: true, brokerage: true };
const BROKERAGE_MONTHLY_SEAT = { boutique: 74, growth: 56, enterprise: 44, franchise: 31 };

const MOTION_CSS = `
.onb-card{transition:background-color 180ms cubic-bezier(.23,1,.32,1),border-color 180ms cubic-bezier(.23,1,.32,1),box-shadow 180ms cubic-bezier(.23,1,.32,1),transform 120ms cubic-bezier(.23,1,.32,1)}
.onb-card:active{transform:scale(.98)}
.onb-press{transition:transform 120ms cubic-bezier(.23,1,.32,1)}
.onb-press:active:not(:disabled){transform:scale(.98)}
.onb-step{animation:onb-in 220ms cubic-bezier(.23,1,.32,1) both}
.onb-li{animation:onb-li 200ms cubic-bezier(.23,1,.32,1) both}
.onb-num{display:inline-block;animation:onb-num 180ms cubic-bezier(.23,1,.32,1) both}
.onb-fig{font-family:"General Sans",ui-sans-serif,-apple-system,sans-serif;font-variant-numeric:tabular-nums;font-feature-settings:"tnum" 1;font-weight:500;letter-spacing:-0.4px;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale}
.onb-stepper input::-webkit-outer-spin-button,.onb-stepper input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
.onb-stepper input{-moz-appearance:textfield}
@media (max-width:560px){.onb-types{grid-template-columns:1fr!important}.onb-types>button{height:64px!important}}
@keyframes onb-in{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
@keyframes onb-li{from{opacity:0;transform:translateY(3px)}to{opacity:1;transform:none}}
@keyframes onb-num{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion:reduce){
.onb-step,.onb-li,.onb-num{animation:onb-fade 140ms linear both}
.onb-card:active,.onb-press:active:not(:disabled){transform:scale(.99)}
@keyframes onb-fade{from{opacity:0}to{opacity:1}}}
`;

// Crossfade/roll a figure whenever its text changes. Tabular nums keep width steady.
function Fig({ children, size = 24, style }) {
  const text = String(children);
  return (
    <span className="onb-fig" style={{ fontSize: size, lineHeight: 1.1, ...style }}>
      <span key={text} className="onb-num">{text}</span>
    </span>
  );
}

function SeatStepper({ value, onChange, min, max, p, label = 'Seats' }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  const clamp = (n) => Math.min(max, Math.max(min, n));
  const commit = (raw) => {
    const n = parseInt(raw, 10);
    onChange(Number.isFinite(n) ? clamp(n) : value);
  };
  const btn = (disabled) => ({
    width: 44, height: 44, border: `1px solid ${p.hair}`, borderRadius: 2, background: p.bg, color: disabled ? p.ink3 : p.ink,
    cursor: disabled ? 'default' : 'pointer', fontSize: 18, lineHeight: 1, fontFamily: F.display, opacity: disabled ? 0.5 : 1,
  });
  return (
    <div className="onb-stepper" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 13, color: p.ink2, minWidth: 44 }}>{label}</span>
      <button type="button" aria-label="Remove a seat" className="onb-press" disabled={value <= min} style={btn(value <= min)} onClick={() => onChange(clamp(value - 1))}>−</button>
      <input
        aria-label={label}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          const n = parseInt(e.target.value, 10);
          if (Number.isFinite(n) && n >= min && n <= max) onChange(n);
        }}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(e.currentTarget.value); }}
        className="onb-fig"
        style={{ width: 72, height: 44, textAlign: 'center', border: `1px solid ${p.hair}`, borderRadius: 2, background: p.bg, color: p.ink, fontSize: 16 }}
      />
      <button type="button" aria-label="Add a seat" className="onb-press" disabled={value >= max} style={btn(value >= max)} onClick={() => onChange(clamp(value + 1))}>+</button>
    </div>
  );
}

function Inclusions({ plan, p }) {
  // Same rows, same order on every card: higher tiers visibly carry more.
  const n = includedCount(plan.id);
  return (
    <div key={plan.id} style={{ display: 'flex', flexDirection: 'column', gap: 0, borderTop: `1px solid ${p.hair}` }}>
      <div className="onb-li" style={{ fontSize: 12, color: p.ink, fontWeight: 500, padding: '10px 0 6px', minHeight: 46 }}>
        {plan.inherits ? `Everything in ${plan.inherits}, plus` : 'Includes'} <span className="onb-fig" style={{ fontSize: 12, color: p.ink3, fontWeight: 400 }}>· {n} of {FEATURE_ROWS.length}</span>
      </div>
      {FEATURE_ROWS.map((r, i) => {
        const st = rowState(r, plan.id);
        const on = st === 'new' || st === 'inherited';
        return (
          <div key={r.label} className="onb-li" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minHeight: 46, padding: '4px 0', animationDelay: `${i * 20}ms` }}>
            <div style={{ width: 14, height: 14, marginTop: 1, borderRadius: 2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: st === 'new' ? T.brick : st === 'inherited' ? p.ink3 : 'transparent', border: on ? 'none' : `1px solid ${p.hair}` }}>
              {on && <I name="chk" size={9} color="#fff" sw={2.5} />}
            </div>
            <span style={{ fontSize: 12, lineHeight: 1.35, color: st === 'new' ? p.ink : st === 'inherited' ? p.ink2 : p.ink3, opacity: st === 'no' ? 0.55 : 1, fontWeight: st === 'new' ? 500 : 400 }}>
              {r.label}
              {st === 'addon' && <span style={{ color: p.ink3 }}> · add-on ${r.addon}/mo</span>}
              {r.note && <span style={{ display: 'block', color: p.ink3, fontWeight: 400 }}>{r.note}</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Audience lock: Brokerage + Team (never Office). Backend already accepts
// subscription_type=brokerage on /auth/set-role; first login stamps brokerage.
const ACCOUNT_TYPES = [
  { id: 'solo', label: 'Individual agent', description: 'One agent, one subscription.' },
  { id: 'team', label: 'Team', description: 'Up to 6 agents on one plan.' },
  { id: 'brokerage', label: 'Brokerage', description: 'Your whole office, priced per seat.' },
  // Property-side account types (common/client_types.py): the property hub.
  { id: 'builder', label: 'Builder or developer', description: 'Site visits, maps and progress reports.', group: 'property' },
  { id: 'property_manager', label: 'Property manager or landlord', description: 'Listing sets, condition reports, turnovers.', group: 'property' },
  { id: 'commercial', label: 'Commercial real estate', description: 'Leasing media, Virtual Visits, aerials.', group: 'property' },
  { id: 'investor', label: 'Investor', description: 'Acquisition captures and portfolio checks.', group: 'property' },
  { id: 'reit_leasing', label: 'REIT', description: 'Every building in one portfolio record.', group: 'property' },
];
const TYPE_GROUPS = [
  { id: 'agents', label: 'Real estate agents', types: ACCOUNT_TYPES.filter((t) => !t.group) },
  { id: 'property', label: 'Property owners, managers and builders', types: ACCOUNT_TYPES.filter((t) => t.group === 'property') },
];

const cad = (cents) => (cents == null ? 'Quoted' : money(Number(cents) / 100));

function formatSeatBand(tier) {
  const seatMin = tier.seat_band_min ?? 1;
  const seatMax = tier.seat_band_max;
  return seatMax ? `${seatMin}–${seatMax} seats` : `${seatMin}+ seats`;
}


export default function OnboardingPage() {
  // Nothing assumed: every user (email, Google, partner) picks an account type. A type the
  // visitor chose on www (?type= on sign-up) is highlighted, and they still confirm it.
  const [accountType, setAccountType] = useState(() => readSignupIntent());
  const navigate = useNavigate();
  const [devCatalog, setDevCatalog] = useState(null);
  const [teamCfg, setTeamCfg] = useState({ included_seats: TEAM_INCLUDED, addon_monthly_cents: null });
  const bodyRef = useRef(null);
  const [bodyH, setBodyH] = useState(null);
  const [billingInterval, setBillingInterval] = useState('month');
  const [planKey, setPlanKey] = useState(null);
  const [brokerageTiers, setBrokerageTiers] = useState([]);
  const [brokerageSeats, setBrokerageSeats] = useState(null);
  const [teamSeats, setTeamSeats] = useState(1);
  const [tiersLoading, setTiersLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [doneHref, setDoneHref] = useState(null);
  const [opening, setOpening] = useState(false);
  const [heard, setHeard] = useState('');
  const [heardDetail, setHeardDetail] = useState('');
  // The app boots from a cached profile; flip needs_onboarding there too so the next page
  // load doesn't bounce back to /onboarding before /auth/me answers.
  const markOnboarded = (updated, subscriptionType) => {
    const next = { ...(user || {}), ...(updated && typeof updated === 'object' ? updated : {}), needs_onboarding: false, subscription_type: subscriptionType };
    try {
      const id = next.user_id || next.id;
      const json = JSON.stringify({ ...next, profile_pending: false });
      localStorage.setItem('bricks-os-profile', json);
      if (id) localStorage.setItem(`bricks-os-profile:${id}`, json);
    } catch {}
    setUser && setUser(next);
  };
  const heardBody = heard ? { heard_source: heard, heard_source_detail: heardDetail.trim() || undefined } : {};
  const { user, setUser } = useAuth();
  const uid = user?.user_id || user?.id;
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  const plans = Object.values(PLAN_DATA);
  const isTeam = accountType === 'team';
  const isBrokerage = accountType === 'brokerage';
  const isDev = isDevSignupType(accountType);
  const devCopy = propertyCopy(accountType);
  const showAnnual = billingInterval === 'year';
  const checkoutInterval = billingInterval;
  const monthlyBlocked = Boolean(accountType) && billingInterval === 'month' && !MONTHLY_ENABLED[accountType];

  const seatMinAll = brokerageTiers.length ? Math.min(...brokerageTiers.map((t) => t.seat_band_min ?? 1)) : 1;
  const seats = brokerageSeats ?? seatMinAll;
  const activeTier = brokerageTiers.find((t) => seats >= (t.seat_band_min ?? 1) && (t.seat_band_max == null || seats <= t.seat_band_max)) || null;
  const seatYear = activeTier ? (Number(activeTier.price_cents) || 0) / 100 : 0;
  const seatMonth = activeTier ? BROKERAGE_MONTHLY_SEAT[activeTier.id] || 0 : 0;
  const brokerageMonthly = isBrokerage && billingInterval === 'month';
  const seatRate = brokerageMonthly ? seatMonth : seatYear;
  const brokerageTotal = Math.round(seatRate * seats * 100) / 100;
  const tierRate = (t) => (brokerageMonthly ? BROKERAGE_MONTHLY_SEAT[t.id] || 0 : (Number(t.price_cents) || 0) / 100);
  const per = brokerageMonthly ? '/seat/mo' : '/seat/yr';
  const nextTier = activeTier ? brokerageTiers.find((t) => (t.seat_band_min ?? 0) > (activeTier.seat_band_min ?? 0)) : null;

  useEffect(() => {
    const id = 'onb-motion';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = MOTION_CSS;
    document.head.appendChild(el);
  }, []);

  useEffect(() => {
    if (!isBrokerage) return;
    let cancelled = false;
    setTiersLoading(true);
    // Public catalog: plain fetch, no auth round-trip needed.
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/software-tiers?tier_kind=brokerage`)
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then((data) => {
        if (cancelled) return;
        const tiers = (Array.isArray(data) ? data : []).slice().sort((a, b) => (a.seat_band_min ?? 0) - (b.seat_band_min ?? 0));
        setBrokerageTiers(tiers);
      })
      .catch(() => {
        if (!cancelled) {
          setBrokerageTiers([]);
          toast.error('Could not load Brokerage plans. Try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setTiersLoading(false);
      });
    return () => { cancelled = true; };
  }, [isBrokerage]);

  useEffect(() => {
    if (!isDev) return;
    if (devCatalog && devCatalog.account_type === accountType) return;
    let live = true;
    setDevCatalog(null);
    // Only what this account type buys (GET /api/dev/catalog, common/client_catalog.py).
    // Prices not yet approved come back empty and read "Priced at booking".
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dev/catalog?type=${encodeURIComponent(accountType)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => { if (live) setDevCatalog(c && Array.isArray(c.families) ? c : { account_type: accountType, families: [] }); })
      .catch(() => { if (live) setDevCatalog({ account_type: accountType, families: [] }); });
    return () => { live = false; };
  }, [isDev, accountType]);

  useEffect(() => {
    if (!isTeam) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/team-seats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => { if (c && c.included_seats) setTeamCfg(c); })
      .catch(() => {});
  }, [isTeam]);

  async function handleFinish() {
    if (isDev) {
      setSaving(true);
      try {
        const { data } = await api.post('/auth/set-role', { subscription_type: accountType, ...heardBody });
        const next = { ...(user || {}), ...(data && typeof data === 'object' ? data : {}), account_type: accountType, needs_onboarding: false };
        try {
          const json = JSON.stringify({ ...next, profile_pending: false });
          localStorage.setItem('bricks-os-profile', json);
          if (uid) localStorage.setItem(`bricks-os-profile:${uid}`, json);
        } catch {}
        setUser && setUser(next);
        clearSignupIntent();
        navigate('/developer/overview', { replace: true });
      } catch (e) {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : 'Could not set up your account. Please try again.');
        setSaving(false);
      }
      return;
    }
    if (isBrokerage) {
      if (!activeTier) return;
      setSaving(true);
      try {
        markOnboarded((await api.post('/auth/set-role', { subscription_type: 'brokerage', ...heardBody })).data, 'brokerage');
        const { data } = await api.post('/billing/subscribe-brokerage', {
          tier_id: activeTier.id,
          seat_count: seats,
          billing_interval: billingInterval,
          origin_url: window.location.origin,
        });
        if (!data?.url) throw new Error('missing checkout url');
        markFinalePending();
        window.location.href = data.url;
      } catch {
        toast.error('Could not start Brokerage checkout. Please try again.');
        setSaving(false);
      }
      return;
    }

    if (!planKey) return;
    setSaving(true);
    try {
      // Only the free tier can activate directly -- paid plans must go through the
      // real Stripe checkout flow (server-side payment verification, see
      // Bricks-OS/backend/server.py's /auth/set-role fix, 2026-07-12).
      if (planKey === 'clay') {
        markOnboarded((await api.post('/auth/set-role', { subscription_type: accountType, plan_key: planKey, ...heardBody })).data, accountType);
        markFinalePending();
        setDoneHref(accountType === 'team' ? '/team/overview' : '/solo/overview');
        setSaving(false);
        return;
      }
      markOnboarded((await api.post('/auth/set-role', { subscription_type: accountType, plan_key: 'clay', ...heardBody })).data, accountType);
      const { data } = await api.post('/billing/checkout', {
        package_id: planKey,
        origin_url: window.location.origin,
        billing_interval: checkoutInterval,
      });
      if (!data?.url) throw new Error('missing checkout url');
      markFinalePending();
      window.location.href = data.url;
    } catch {
      toast.error('Could not save your selection. Please try again.');
      setSaving(false);
    }
  }

  const canFinish = isDev || !monthlyBlocked && (isBrokerage
    ? Boolean(activeTier) && !tiersLoading
    : Boolean(planKey));

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setBodyH(Math.ceil(e.contentRect.height)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [doneHref]);

  const rawName = (user?.name || user?.full_name || '').trim();
  const emailLocal = String(user?.email || '').split('@')[0];
  // Only greet by name when it's a real name (Google/partner profile), not the email prefix.
  const firstName = rawName && rawName.toLowerCase() !== emailLocal.toLowerCase() ? rawName.split(/\s+/)[0] : '';

  const cardStyle = (selected) => ({
    padding: 'var(--bx-pad)',
    border: `1px solid ${selected ? p.ink : p.hair}`,
    boxShadow: selected ? `inset 0 0 0 1px ${p.ink}` : 'inset 0 0 0 1px transparent',
    borderRadius: 2,
    cursor: 'pointer',
    background: selected ? p.surf2 : p.bg,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    textAlign: 'left',
    color: p.ink,
    fontFamily: F.display,
  });


  if (doneHref) {
    return (
      <div style={{ minHeight: '100vh', background: p.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: F.display, color: p.ink, padding: '48px var(--bx-pad)', WebkitFontSmoothing: 'antialiased' }}>
        <div className="onb-step" style={{ width: '100%', maxWidth: 480 }}>
          <div style={{ marginBottom: 48 }}><Wordmark size={19} color={p.ink} /></div>
          <div style={{ fontSize: 28, fontWeight: 500, letterSpacing: -0.6 }}>You're set up</div>
          <div style={{ fontSize: 14, color: p.ink2, marginTop: 8 }}>You're on Clay. Upgrade anytime in Billing.</div>
          <div style={{ marginTop: 32 }}>
            <Btn variant="accent" dark={dark} full className="onb-press" disabled={opening} aria-busy={opening}
              onClick={() => { setOpening(true); buildWallThenGo(uid, doneHref); }}>
              {opening ? (<><BricksLoader size={16} color="currentColor" label="Opening dashboard" /> Opening dashboard…</>) : 'Open dashboard'}
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: p.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', fontFamily: F.display, color: p.ink, padding: 'clamp(48px, 10vh, 96px) 24px 48px', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}>

      <div className="onb-step" style={{ marginBottom: 48 }}><Wordmark size={19} color={p.ink} /></div>

      {/* Account type selector */}
      <div className="onb-step" style={{ width: '100%', maxWidth: 820, marginBottom: 32 }}>
        <div style={{ fontSize: 26, fontWeight: 500, letterSpacing: -0.5, marginBottom: 8 }}>{firstName ? `Welcome, ${firstName}` : 'Welcome to Bricks'}</div>
        <div style={{ fontSize: 14, color: p.ink2, marginBottom: 16 }}>How will you use Bricks?</div>
        <div role="radiogroup" aria-label="Account type" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {TYPE_GROUPS.map((g) => (
        <div key={g.id}>
        <div style={{ fontSize: 12, color: p.ink3, marginBottom: 8 }}>{g.label}</div>
        <div className="onb-types" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
          {g.types.map(type => {
            const sel = accountType === type.id;
            return (
              <button
                type="button"
                role="radio"
                aria-checked={sel}
                key={type.id}
                className="onb-card"
                onClick={() => {
                  setAccountType(type.id);
                  clearSignupIntent();
                  if (type.id === 'brokerage') setPlanKey(null);
                }}
                style={{ ...cardStyle(sel), padding: '14px 20px', gap: 4, height: 84, justifyContent: 'flex-start', overflow: 'hidden' }}
              >
                <div style={{ fontSize: 13, fontWeight: 500, color: p.ink }}>{type.label}</div>
                <div style={{ fontSize: 12, color: p.ink3 }}>{type.description}</div>
              </button>
            );
          })}
        </div>
        </div>
        ))}
        </div>
      </div>

      {/* Plan selection — only after an account type is chosen; height eases, nothing below jumps */}
      <div style={{ width: '100%', maxWidth: 820, height: bodyH ?? 'auto', overflow: 'hidden', transition: 'height 220ms cubic-bezier(.23,1,.32,1)' }}>
      <div ref={bodyRef}>
      {accountType && (
      <div key={accountType} className="onb-step" style={{ width: '100%' }}>
        {isDev ? (
          <div>
            <div style={{ fontSize: 26, fontWeight: 500, letterSpacing: -0.5, marginBottom: 8 }}>{devCopy.onboardTitle}</div>
            <div style={{ fontSize: 14, color: p.ink2, marginBottom: 24, maxWidth: 560 }}>
              {devCopy.onboardBody} Every visit is dated and filed under its {devCopy.thing}. Free to set up; you pay only for what you book.
            </div>
            <div style={{ fontSize: 12, color: p.ink3, marginBottom: 16 }}>Anything you booked on brxs.ca with {user?.email || 'this email'} shows up in your hub.</div>
            {devCatalog == null ? (
              <div style={{ fontSize: 13, color: p.ink3, display: 'flex', alignItems: 'center', gap: 8 }}><BricksLoader size={16} label="Loading prices" /> Loading prices…</div>
            ) : !devCatalog.families.length ? (
              <div style={{ fontSize: 13, color: p.ink3 }}>Prices are shown when you book.</div>
            ) : (
              <div data-testid="onb-dev-catalog" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                {devCatalog.families.flatMap((f) => (f.key === 'site_progress'
                  ? [{ ...f, key: 'sp_visits', title: 'Single site visits', items: f.items.filter((x) => x.kind === 'one_off' && x.key !== 'mapped_setup') },
                     { ...f, key: 'sp_plans', title: 'Site progress plans', items: f.items.filter((x) => x.kind === 'plan') }]
                  : [f])).map((f) => (
                  <div key={f.key} style={{ border: `1px solid ${p.hair}`, borderRadius: 2, padding: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>{f.title}</div>
                    {f.items.map((x) => (
                      <div key={x.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '6px 0', borderTop: `1px solid ${p.hair}` }}>
                        <span style={{ color: p.ink2, minWidth: 0 }}>{x.name}</span>
                        <span style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                          {x.price_cad_cents == null
                            ? <span style={{ color: p.ink3 }}>{x.price_label || 'Quoted'}</span>
                            : <><Fig size={13}>{cad(x.price_cad_cents)}</Fig>{x.kind === 'plan' && <span style={{ color: p.ink3 }}> /mo</span>}</>}
                        </span>
                      </div>
                    ))}
                    <div style={{ fontSize: 12, color: p.ink3, marginTop: 8 }}>
                      {f.key === 'sp_plans' ? 'Per site, month to month. Mapped plans add a one-time ground control setup.'
                        : f.key === 'sp_visits' ? 'Survey-grade work is quoted.'
                        : f.live ? 'Volume pricing on larger portfolios.' : 'We confirm the price for your property when you book.'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (<>
        <div style={{ fontSize: 26, fontWeight: 500, letterSpacing: -0.5, marginBottom: 8 }}>{isBrokerage ? 'Choose your seats' : 'Choose your plan'}</div>
        <div style={{ fontSize: 14, color: p.ink2, marginBottom: 24 }}>
          {isBrokerage
            ? (brokerageMonthly ? 'Per seat, month to month. Cancel anytime.' : 'Per seat, billed yearly. Agents don’t pay.')
            : isTeam
              ? (showAnnual ? 'Billed yearly. 2 months free.' : 'Billed monthly. Cancel anytime.')
              : (showAnnual
                ? 'Billed yearly. 2 months free.'
                : 'Billed monthly. Cancel anytime.')}
        </div>

        <div role="radiogroup" aria-label="Billing interval" style={{ position: 'relative', display: 'inline-grid', gridTemplateColumns: '1fr 1fr', border: `1px solid ${p.hair}`, borderRadius: 2, padding: 2, marginBottom: 24 }}>
          <span aria-hidden="true" style={{ position: 'absolute', top: 2, bottom: 2, left: 2, width: 'calc(50% - 2px)', background: p.ink, borderRadius: 2, transform: billingInterval === 'year' ? 'translateX(100%)' : 'none', transition: 'transform 200ms cubic-bezier(.23,1,.32,1)' }} />
          {[{ id: 'month', label: 'Monthly' }, { id: 'year', label: isBrokerage ? 'Yearly' : 'Annual · 2 months free' }].map(opt => {
            const sel = billingInterval === opt.id;
            return (
              <button type="button" role="radio" aria-checked={sel} key={opt.id} className="onb-press"
                onClick={() => setBillingInterval(opt.id)}
                style={{ position: 'relative', zIndex: 1, padding: '0 16px', minHeight: 44, border: 0, background: 'transparent', cursor: 'pointer', fontFamily: F.display, fontSize: 13, fontWeight: 500, color: sel ? p.bg : p.ink, transition: 'color 180ms cubic-bezier(.23,1,.32,1)', whiteSpace: 'nowrap' }}>
                {opt.label}
              </button>
            );
          })}
        </div>
        {monthlyBlocked && (
          <div className="onb-li" style={{ fontSize: 13, color: p.ink2, marginTop: -12, marginBottom: 20 }}>
            Brokerage is billed yearly for now.
          </div>
        )}

        {isBrokerage ? (
          tiersLoading ? (
            <div style={{ fontSize: 13, color: p.ink3, display: 'flex', alignItems: 'center', gap: 8 }}><BricksLoader size={16} label="Loading Brokerage plans" /> Loading Brokerage plans…</div>
          ) : brokerageTiers.length === 0 ? (
            <div style={{ fontSize: 13, color: p.ink3 }}>Plans didn’t load. Try again shortly.</div>
          ) : (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: 20, border: `1px solid ${p.hair}`, borderRadius: 2, marginBottom: 12 }}>
                <div>
                  <SeatStepper value={seats} min={seatMinAll} max={SEAT_MAX} onChange={setBrokerageSeats} p={p} />
                  <div style={{ fontSize: 12, color: p.ink3, marginTop: 8 }}>
                    Minimum {seatMinAll} seats. Change anytime in Billing.
                  </div>
                  {nextTier && (
                    <div key={nextTier.id} className="onb-li" style={{ fontSize: 12, color: p.ink2, marginTop: 4 }}>
                      {`${(nextTier.seat_band_min ?? 0) - seats} more seat${(nextTier.seat_band_min ?? 0) - seats === 1 ? '' : 's'} moves you to ${nextTier.name} at ${money(tierRate(nextTier))}${per}.`}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: p.ink3 }}>{activeTier ? `${activeTier.name} · ${seats} × ${money(seatRate)}${per}` : 'Contact us'}</div>
                  <div style={{ marginTop: 4 }}><Fig size={28}>{money(brokerageTotal)}</Fig><span style={{ fontSize: 12, color: p.ink3 }}>{brokerageMonthly ? ' /mo' : ' /yr'}</span></div>
                  <div style={{ fontSize: 12, color: p.ink3, marginTop: 2 }}>{brokerageMonthly ? 'Month to month. Cancel anytime.' : 'Billed yearly.'}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
                {brokerageTiers.map((tier) => {
                  const selected = activeTier?.id === tier.id;
                  const min = tier.seat_band_min ?? 1;
                  return (
                    <button type="button" key={tier.id} className="onb-card" aria-pressed={selected}
                      onClick={() => setBrokerageSeats(min)}
                      style={cardStyle(selected)}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: p.ink }}>{tier.name}</div>
                        <div style={{ marginTop: 10 }}><Fig size={22}>{money(tierRate(tier))}</Fig><span style={{ fontSize: 12, color: p.ink3 }}> {per}</span></div>
                        <div style={{ fontSize: 12, color: p.ink3, marginTop: 6 }}>{formatSeatBand(tier)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                <span style={{ fontSize: 12, color: p.ink, fontWeight: 500, width: '100%' }}>Every seat includes</span>
                {OFFICE_INCLUDES.map((f, i) => (
                  <span key={f} className="onb-li" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: p.ink2, animationDelay: `${i * 25}ms` }}>
                    <span style={{ width: 14, height: 14, borderRadius: 2, background: T.ok, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><I name="chk" size={9} color="#fff" sw={2.5} /></span>{f}
                  </span>
                ))}
              </div>
            </>
          )
        ) : (
          <>
            {isTeam && (
              <div style={{ padding: 20, border: `1px solid ${p.hair}`, borderRadius: 2, marginBottom: 12 }}>
                <SeatStepper value={Math.min(teamSeats, teamCfg.included_seats)} min={1} max={teamCfg.included_seats} onChange={setTeamSeats} p={p} label="Agents" />
                <div data-testid="onb-team-cap" style={{ fontSize: 12, color: p.ink3, marginTop: 8 }}>
                  {`One plan covers up to ${teamCfg.included_seats} agents. `}
                  {teamCfg.addon_monthly_cents
                    ? `Each agent after that is ${money(teamCfg.addon_monthly_cents / 100)}/mo.`
                    : 'More agents? Add seats later in Billing, or choose Brokerage.'}
                </div>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              {plans.map(plan => {
                const sel = planKey === plan.id;
                return (
                  <button type="button" key={plan.id} className="onb-card" aria-pressed={sel}
                    onClick={() => setPlanKey(plan.id)} style={{ ...cardStyle(sel), justifyContent: 'flex-start', height: '100%' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minHeight: 20 }}>
                        <span style={{ fontSize: 14, fontWeight: 500, color: p.ink }}>{plan.name}</span>
                        {plan.id === RECOMMENDED_PLAN && <span style={{ fontSize: 12, color: p.ink2 }}>Recommended</span>}
                      </div>
                      <div style={{ marginTop: 10 }}>
                        {plan.price === 0 ? (
                          <Fig size={24}>Free</Fig>
                        ) : (
                          <><Fig size={24}>{money(showAnnual ? plan.annualPrice : plan.price)}</Fig><span style={{ fontSize: 12, color: p.ink3 }}>{showAnnual ? '/yr' : '/mo'}</span></>
                        )}
                      </div>
                      <div key={`${plan.id}-${showAnnual}`} className="onb-li" style={{ fontSize: 12, color: p.ink3, marginTop: 6, lineHeight: 1.4, minHeight: 34 }}>{planValueLine(plan, showAnnual)}</div>
                    </div>
                    <Inclusions plan={plan} p={p} />
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: p.ink3, marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              AI add-ons on any paid plan. Included in Diamond.
              <InfoTip label="AI add-on prices">{AI_ADDONS.map((a) => `${a.name} $${a.monthly}/mo`).join(' · ')}</InfoTip>
            </div>
                  </>
        )}

        <div style={{ fontSize: 12, color: p.ink3, marginTop: 16 }}>Media is booked separately, with volume discounts.</div>
        </>)}
        <div style={{ marginTop: 24, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: p.ink2 }}>
            How did you hear about Bricks? (optional)
            <select value={heard} onChange={(e) => setHeard(e.target.value)}
              style={{ height: 44, minWidth: 240, padding: '0 10px', border: `1px solid ${p.hair}`, borderRadius: 2, background: p.bg, color: p.ink, fontFamily: F.display, fontSize: 13 }}>
              <option value="">Choose one</option>
              {HEARD_OPTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </label>
          {(heard === 'other' || heard.startsWith('referral_')) && (
            <input className="onb-li" aria-label={heard === 'other' ? 'Where did you hear about us?' : 'Who referred you?'} placeholder={heard === 'other' ? 'Where? (optional)' : 'Who referred you? (optional)'}
              maxLength={80} value={heardDetail} onChange={(e) => setHeardDetail(e.target.value)}
              style={{ height: 44, minWidth: 220, padding: '0 10px', border: `1px solid ${p.hair}`, borderRadius: 2, background: p.bg, color: p.ink, fontFamily: F.display, fontSize: 13 }} />
          )}
        </div>
        <div style={{ marginTop: 24, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <Btn variant="accent" dark={dark} className="onb-press" onClick={handleFinish} disabled={!canFinish || saving} aria-busy={saving}
            style={{ opacity: !canFinish && !saving ? 0.5 : 1, minWidth: 200 }}>
            {saving ? (<><BricksLoader size={16} color="currentColor" label="Setting up" /> Setting up…</>) : (isDev ? 'Open your hub' : isBrokerage ? 'Continue to checkout' : 'Get started')}
          </Btn>
          {isDev && (
            <a href={devCopy.book.href} className="onb-press" style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, padding: '0 16px', border: `1px solid ${p.hair}`, borderRadius: 2, color: p.ink, fontSize: 13, fontWeight: 500, textDecoration: 'none', fontFamily: F.display }}>
              {devCopy.book.label}
            </a>
          )}
        </div>
      </div>
      )}
      </div>
      </div>

    </div>
  );
}
